import { useCallback, useEffect, useRef, useState } from 'react';
import type { Event as PolyEvent, PaginationCursor } from '@polymarket/client';

import { getPolyClient } from '../utils/client';
import {
  buildListEventsRequest,
  extractTokenIds,
  sortEventMarkets,
  PAGE_SIZE,
  type LiveUpdate,
  type SortValue,
  type StatusValue,
} from '../utils/api';

export type UsePolyEventsResult = {
  events: PolyEvent[];
  liveByTokenId: Record<string, LiveUpdate>;
  hasMore: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
  error: unknown;
  loadMore: () => void;
};

type Filters = { sort: SortValue; status: StatusValue; slugId?: string; search?: string };

export function usePolyEvents({ sort, status, slugId, search }: Filters): UsePolyEventsResult {
  const [events, setEvents] = useState<PolyEvent[]>([]);
  const [cursor, setCursor] = useState<PaginationCursor | undefined>(undefined);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [liveByTokenId, setLiveByTokenId] = useState<Record<string, LiveUpdate>>({});

  const requestSeq = useRef(0);

  useEffect(() => {
    const myId = ++requestSeq.current;
    setIsLoading(true);
    setError(null);
    setEvents([]);
    setCursor(undefined);
    setHasMore(false);
    setLiveByTokenId({});

    const client = getPolyClient();
    const request = buildListEventsRequest({ sort, status, slugId, search, pageSize: PAGE_SIZE });

    client
      .listEvents(request)
      .firstPage()
      .then((page) => {
        if (myId !== requestSeq.current) return;
        setEvents(page.items.map((e) => sortEventMarkets(e)));
        setHasMore(page.hasMore);
        setCursor(page.nextCursor);
        setIsLoading(false);
      })
      .catch((err) => {
        if (myId !== requestSeq.current) return;
        setError(err);
        setIsLoading(false);
      });
  }, [sort, status, slugId, search]);

  const tokenKey = extractTokenIds(events).sort().join(',');

  useEffect(() => {
    if (!tokenKey) return;
    const tokenIds = tokenKey.split(',');

    let cancelled = false;
    let handle: { close: () => Promise<void> } | null = null;

    (async () => {
      try {
        const client = getPolyClient();
        const sub = await client.subscribe([{ topic: 'market', tokenIds }]);
        if (cancelled) {
          await sub.close();
          return;
        }
        handle = sub;
        for await (const event of sub) {
          if (cancelled) break;
          const update = event as unknown as LiveUpdate;
          const tokenId = update?.payload?.tokenId;
          if (!tokenId) continue;
          setLiveByTokenId((prev) => ({ ...prev, [tokenId]: update }));
        }
      } catch (err) {
        if (!cancelled) console.error('PolyPredictionsEvents subscribe error:', err);
      }
    })();

    return () => {
      cancelled = true;
      if (handle) handle.close().catch(() => {});
    };
  }, [tokenKey]);

  const loadMore = useCallback(() => {
    if (!cursor || isLoading || isLoadingMore) return;
    const myId = requestSeq.current;
    setIsLoadingMore(true);

    const client = getPolyClient();
    const request = buildListEventsRequest({ sort, status, slugId, search, pageSize: PAGE_SIZE });

    client
      .listEvents(request)
      .from(cursor)
      .firstPage()
      .then((page) => {
        if (myId !== requestSeq.current) return;
        setEvents((prev) => [...prev, ...page.items.map((e) => sortEventMarkets(e))]);
        setHasMore(page.hasMore);
        setCursor(page.nextCursor);
        setIsLoadingMore(false);
      })
      .catch((err) => {
        if (myId !== requestSeq.current) return;
        setError(err);
        setIsLoadingMore(false);
      });
  }, [cursor, isLoading, isLoadingMore, sort, status, slugId, search]);

  return { events, liveByTokenId, hasMore, isLoading, isLoadingMore, error, loadMore };
}
